import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpRight,
  BrainCircuit,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Crown,
  LoaderCircle,
  Lock,
  Sparkles,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useI18n } from '@/contexts/I18nContext';
import { insightsService } from '@/services/storageService';
import type { InsightStatsSnapshot, PackageType, WeeklyInsight } from '@/types';

type PlayerInsightsPanelProps = {
  clubId: number;
  packageCode?: PackageType | string;
  playerName?: string;
};

const FETCH_LIMIT = 40;

const PLAN_COPY = {
  package1: {
    label: 'Starter',
    clearInsightsPerWeek: 0,
    heroTitle: 'Unlock weekly AI insights',
    description: 'Upgrade your plan to unlock complete AI-driven performance insights.',
    archiveHint: 'Upgrade to Pro or Elite to reveal insight text.',
  },
  package2: {
    label: 'Pro',
    clearInsightsPerWeek: 1,
    heroTitle: 'Your weekly AI insight',
    description: 'Get a concise weekly AI insight designed to highlight your key performance trends.',
    archiveHint: 'Upgrade to Elite to reveal every insight for the week.',
  },
  package3: {
    label: 'Elite',
    clearInsightsPerWeek: Number.MAX_SAFE_INTEGER,
    heroTitle: 'Your AI insights timeline',
    description: 'Explore multiple AI-powered insights each week for a complete performance perspective.',
    archiveHint: 'Use the arrows to move through previous week insights.',
  },
} as const;

function normalizePackageCode(value?: string) {
  const normalized = String(value || 'package1').trim().toLowerCase();
  if (normalized === 'package3') return 'package3';
  if (normalized === 'package2') return 'package2';
  return 'package1';
}

function cleanInsightText(value: string) {
  return String(value || '')
    .replace(/\r/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function toPreview(value: string, limit = 180) {
  const cleaned = cleanInsightText(value);
  if (cleaned.length <= limit) return cleaned;
  return `${cleaned.slice(0, limit).trimEnd()}...`;
}

function formatWeekLabel(weekLabel: string) {
  const match = /^(\d{4})-W(\d{2})$/.exec(weekLabel || '');
  if (!match) return weekLabel || 'Weekly insight';
  return `Week ${Number(match[2])}, ${match[1]}`;
}

function formatGeneratedDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Recently generated';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getCurrentWeekLabel(date = new Date()) {
  const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNumber + 3);

  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3);

  const weekNumber = 1 + Math.round((target.getTime() - firstThursday.getTime()) / 604800000);
  return `${target.getUTCFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
}

function buildSnapshotPills(snapshot?: InsightStatsSnapshot | null) {
  if (!snapshot) return [];

  const pills: { label: string; value: string }[] = [];

  if (typeof snapshot.win_rate_pct === 'number') {
    pills.push({ label: 'Win Rate', value: `${snapshot.win_rate_pct}%` });
  }

  if (typeof snapshot.matches_played === 'number') {
    pills.push({ label: 'Matches', value: String(snapshot.matches_played) });
  }

  if (typeof snapshot.total_court_seconds === 'number') {
    pills.push({
      label: 'Court Time',
      value: `${Math.round(snapshot.total_court_seconds / 60)} min`,
    });
  }

  if (snapshot.current_rank) {
    pills.push({ label: 'Rank', value: `#${snapshot.current_rank}` });
  }

  if (typeof snapshot.evaluation_avg_rating === 'number') {
    pills.push({ label: 'Eval', value: snapshot.evaluation_avg_rating.toFixed(1) });
  }

  return pills.slice(0, 4);
}

function InsightLoadingState() {
  return (
    <Card className="overflow-hidden border-primary/20 shadow-lg">
      <CardContent className="p-0">
        <div
          className="p-6 sm:p-8"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--primary) / 0.10) 45%, hsl(var(--accent) / 0.14) 100%)',
          }}
        >
          <Skeleton className="h-6 w-44" />
          <Skeleton className="mt-4 h-10 w-full max-w-2xl" />
          <Skeleton className="mt-3 h-5 w-full max-w-xl" />
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <Skeleton className="h-24 rounded-[24px]" />
            <Skeleton className="h-24 rounded-[24px]" />
            <Skeleton className="h-24 rounded-[24px]" />
          </div>
        </div>

        <div className="p-6">
          <Skeleton className="h-[360px] rounded-[30px]" />
        </div>
      </CardContent>
    </Card>
  );
}

type WeekInsightGroup = {
  weekLabel: string;
  generatedAt: string;
  insights: WeeklyInsight[];
};

function groupInsightsByWeek(insights: WeeklyInsight[]) {
  const sorted = [...insights].sort((a, b) => {
    const aTime = new Date(a.generated_at).getTime();
    const bTime = new Date(b.generated_at).getTime();
    return bTime - aTime;
  });

  const groups = new Map<string, WeekInsightGroup>();
  const order: string[] = [];

  sorted.forEach((insight) => {
    const key = insight.week_label || 'weekly-insight';
    const group = groups.get(key);

    if (group) {
      group.insights.push(insight);
      return;
    }

    order.push(key);
    groups.set(key, {
      weekLabel: key,
      generatedAt: insight.generated_at,
      insights: [insight],
    });
  });

  return order
    .map((key) => groups.get(key))
    .filter((group): group is WeekInsightGroup => Boolean(group));
}

function BlurredInsightCard({
  insight,
  cardIndex,
}: {
  insight: WeeklyInsight;
  cardIndex: number;
}) {
  return (
    <div className="relative overflow-hidden rounded-[26px] border border-primary/15 bg-card p-5 shadow-sm">
      <div className="absolute inset-0 bg-background/68 backdrop-blur-sm" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-muted-foreground/90">Insight #{cardIndex + 1}</p>
            <p className="mt-1 text-xs text-muted-foreground/80">{formatGeneratedDate(insight.generated_at)}</p>
          </div>
          <Badge className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">
            <Lock className="mr-1 h-3.5 w-3.5" />
            Locked
          </Badge>
        </div>

        <p className="mt-4 text-sm leading-6 text-muted-foreground/85">{toPreview(insight.insight_text, 170)}</p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
          Upgrade your plan to see full insight
        </p>
      </div>
    </div>
  );
}

function VisibleInsightCard({
  insight,
  cardIndex,
}: {
  insight: WeeklyInsight;
  cardIndex: number;
}) {
  return (
    <div className="rounded-[26px] border border-primary/15 bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Insight #{cardIndex + 1}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">{formatGeneratedDate(insight.generated_at)}</p>
        </div>
        <Badge className="border-primary/10 bg-primary/10 text-primary hover:bg-primary/10">Visible</Badge>
      </div>
      <p className="mt-4 text-base leading-7 text-foreground">{toPreview(insight.insight_text, 350)}</p>
    </div>
  );
}

export default function PlayerInsightsPanel({
  clubId,
  packageCode,
  playerName,
}: PlayerInsightsPanelProps) {
  const { t, language } = useI18n();
  const queryClient = useQueryClient();
  const normalizedPackage = normalizePackageCode(packageCode);
  const plan = PLAN_COPY[normalizedPackage];
  const clearInsightsPerWeek = plan.clearInsightsPerWeek;
  const canGenerateInsight = normalizedPackage !== 'package1';

  const insightsQuery = useQuery({
    queryKey: ['player-insights', clubId, language],
    queryFn: () => insightsService.listMine(clubId, FETCH_LIMIT, 0, language),
    enabled: Boolean(clubId),
  });

  const generateInsightMutation = useMutation({
    mutationFn: () => insightsService.generateMine(clubId),
    onSuccess: async () => {
      toast.success(t('insight_weekly_ready', 'Your weekly insight is ready.'));
      await queryClient.invalidateQueries({ queryKey: ['player-insights', clubId] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t('insight_unable_generate', 'Unable to generate an insight right now.'));
    },
  });

  const insights = React.useMemo(
    () => insightsQuery.data?.items ?? [],
    [insightsQuery.data?.items],
  );
  const weekGroups = React.useMemo(() => groupInsightsByWeek(insights), [insights]);
  const [activeWeekIndex, setActiveWeekIndex] = React.useState(0);
  const firstWeekKey = weekGroups[0]?.weekLabel;

  React.useEffect(() => {
    if (!weekGroups.length) {
      if (activeWeekIndex !== 0) setActiveWeekIndex(0);
      return;
    }

    if (activeWeekIndex >= weekGroups.length) {
      setActiveWeekIndex(0);
    }
  }, [activeWeekIndex, weekGroups.length]);

  React.useEffect(() => {
    setActiveWeekIndex(0);
  }, [firstWeekKey]);

  const activeWeek = weekGroups[activeWeekIndex];
  const activeWeekInsights = activeWeek?.insights ?? [];
  const visibleInsightCount = Math.min(activeWeekInsights.length, clearInsightsPerWeek);
  const hiddenInsightCount = Math.max(activeWeekInsights.length - visibleInsightCount, 0);
  const featuredInsight = activeWeekInsights[0];
  const featuredPills = buildSnapshotPills(featuredInsight?.stats_snapshot);
  const hasWeekControls = weekGroups.length > 1;
  const currentWeekLabel = getCurrentWeekLabel();
  const hasCurrentWeekInsight = weekGroups.some((group) => group.weekLabel === currentWeekLabel);

  if (insightsQuery.isLoading) {
    return <InsightLoadingState />;
  }

  const moveToPreviousWeek = () => {
    if (!hasWeekControls) return;
    setActiveWeekIndex((previous) =>
      previous === 0 ? weekGroups.length - 1 : previous - 1,
    );
  };

  const moveToNextWeek = () => {
    if (!hasWeekControls) return;
    setActiveWeekIndex((previous) =>
      previous === weekGroups.length - 1 ? 0 : previous + 1,
    );
  };

  const hasInsightsForSelectedWeek = activeWeekInsights.length > 0;
  const upgradeHint =
    normalizedPackage === 'package1'
      ? 'Insights are masked in Starter. Upgrade to Pro or Elite for full weekly details.'
      : 'Some insights are blurred on Pro. Upgrade to Elite to view every insight for this week.';

  return (
    <Card className="overflow-hidden border-primary/20 shadow-lg">
      <CardContent className="p-0">
        <div
          className="border-b border-border/70 p-6 sm:p-8"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--primary) / 0.12) 45%, hsl(var(--accent) / 0.18) 100%)',
          }}
        >
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <Badge className="border-primary/10 bg-primary/10 text-primary hover:bg-primary/10">
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                AI Coaching Insights
              </Badge>

              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {playerName ? `${playerName}, ${plan.heroTitle.toLowerCase()}` : plan.heroTitle}
              </h2>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                {plan.description}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row xl:flex-col xl:items-stretch">
              <Badge className="justify-center border-amber-200 bg-amber-50 px-3 py-2 text-amber-700 hover:bg-amber-50">
                <Crown className="mr-1.5 h-3.5 w-3.5" />
                {plan.label} Plan
              </Badge>

              {canGenerateInsight ? (
                <Button
                  type="button"
                  className="min-w-[220px]"
                  onClick={() => generateInsightMutation.mutate()}
                  disabled={generateInsightMutation.isPending || hasCurrentWeekInsight}
                >
                  {generateInsightMutation.isPending ? (
                    <LoaderCircle className="animate-spin" />
                  ) : (
                    <Zap />
                  )}
                  {hasCurrentWeekInsight
                    ? t('insight_already_generated_week', 'Already generated this week')
                    : t('insight_generate_weekly', 'Generate weekly insight')}
                </Button>
              ) : (
                <Button asChild className="min-w-[220px]">
                  <Link to="/dashboard/packages">
                    Upgrade to unlock
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>

        </div>

        <div className="p-6">
          {!hasInsightsForSelectedWeek ? (
            <div className="rounded-[30px] border border-primary/15 bg-card p-6 shadow-sm">
              <Badge className="border-primary/10 bg-primary/10 text-primary hover:bg-primary/10">
                <BrainCircuit className="mr-1.5 h-3.5 w-3.5" />
                First AI Insight
              </Badge>

              <h3 className="mt-4 text-2xl font-semibold text-foreground">
                Generate your first weekly coaching summary.
              </h3>

              <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground">
                Your insight will use real match stats, court activity, ranking movement,
                evaluations, and equipment usage to produce a short coaching takeaway.
              </p>

              {canGenerateInsight ? (
                <Button
                  type="button"
                  className="mt-5"
                  onClick={() => generateInsightMutation.mutate()}
                  disabled={generateInsightMutation.isPending}
                >
                  {generateInsightMutation.isPending ? (
                    <LoaderCircle className="animate-spin" />
                  ) : (
                    <BrainCircuit />
                  )}
                  Generate My Weekly Insight
                </Button>
              ) : (
                <Button asChild className="mt-5">
                  <Link to="/dashboard/packages">Upgrade to view insights</Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              <div className="overflow-hidden rounded-[30px] border border-primary/15 bg-card shadow-sm">
                <div
                  className="border-b border-border/70 p-6"
                  style={{
                    background:
                      'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--primary) / 0.10) 55%, hsl(var(--accent) / 0.18) 100%)',
                  }}
                >
                  <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">
                        Weekly Insights
                      </p>

                      <h3 className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
                        {formatWeekLabel(activeWeek.weekLabel)}
                      </h3>

                      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays className="h-4 w-4 text-primary" />
                          {formatGeneratedDate(activeWeek.generatedAt)}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Sparkles className="h-4 w-4 text-primary" />
                          {t('weekly_insights_count', '{count} insights in this week').replace(
                            '{count}',
                            String(activeWeekInsights.length),
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-start gap-3 lg:items-end">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={moveToPreviousWeek}
                          disabled={!hasWeekControls}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <p className="min-w-20 text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                          {activeWeekIndex + 1} / {weekGroups.length}
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={moveToNextWeek}
                          disabled={!hasWeekControls}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {hasWeekControls
                          ? 'Use arrows to move through previous week insights.'
                          : 'No previous week insights yet.'}
                      </p>
                    </div>
                  </div>

                  {featuredPills.length ? (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {featuredPills.map((pill) => (
                        <div
                          key={pill.label}
                          className="rounded-full border border-primary/12 bg-background/90 px-3 py-2 text-sm shadow-sm"
                        >
                          <span className="text-muted-foreground">{pill.label}</span>
                          <span className="ml-2 font-semibold text-foreground">{pill.value}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="p-6">
                  <div className="mb-5 flex items-center gap-3">
                    <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <TrendingUp className="h-5 w-5" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                      Coaching Takeaways
                    </p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {activeWeekInsights.map((insight, index) => {
                      const canView = index < visibleInsightCount;
                      if (canView) {
                        return <VisibleInsightCard key={insight.id} insight={insight} cardIndex={index} />;
                      }

                      return <BlurredInsightCard key={insight.id} insight={insight} cardIndex={index} />;
                    })}
                  </div>
                </div>
              </div>

              {hiddenInsightCount > 0 ? (
                <div className="flex flex-col gap-3 rounded-[24px] border border-amber-200 bg-amber-50/70 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-amber-800">{upgradeHint}</p>
                  <Button
                    asChild
                    variant="outline"
                    className="border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                  >
                    <Link to="/dashboard/packages">
                      {normalizedPackage === 'package1' ? 'Upgrade to Pro or Elite' : 'Upgrade to Elite'}
                    </Link>
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
